import { getPreviewUrls, PreviewUrlEntry } from "../services/files.service";
import { timedOrdersFetch } from "../utils/ordersPerfLog";
import { defineStore } from "pinia";

export const useFilesStore = defineStore("files", {
  state: () => ({
    urlCache: new Map<number, PreviewUrlEntry>(),
    pendingIds: new Set<number>(),
    coalesceTimer: null as ReturnType<typeof setTimeout> | null,
    coalesceWaiters: [] as Array<{
      ids: number[];
      resolve: (urls: Record<number, string>) => void;
      reject: (error: unknown) => void;
    }>,
  }),
  actions: {
    invalidatePreviewUrls(previewFileIds: number[]) {
      for (const id of previewFileIds) this.urlCache.delete(id);
    },

    async getPreviewUrl(previewFileId: number): Promise<string> {
      const urls = await this.fetchPreviewUrls([previewFileId]);
      return urls[previewFileId] || "";
    },

    /** Collect ids requested in the same frame and send one POST. */
    async fetchPreviewUrls(previewFileIds: number[]): Promise<Record<number, string>> {
      const uniqueIds = [...new Set(previewFileIds.filter((id) => id > 0))];
      if (!uniqueIds.length) return {};

      const cachedResult: Record<number, string> = {};
      const idsToFetch = uniqueIds.filter((id) => {
        const cached = this.urlCache.get(id);
        if (cached && cached.expiresAt > Date.now() + 60_000) {
          cachedResult[id] = cached.url;
          return false;
        }
        return true;
      });

      if (!idsToFetch.length) return cachedResult;

      return new Promise((resolve, reject) => {
        for (const id of idsToFetch) this.pendingIds.add(id);
        this.coalesceWaiters.push({ ids: uniqueIds, resolve, reject });

        if (this.coalesceTimer) return;
        this.coalesceTimer = setTimeout(() => {
          void this.flushPreviewUrlBatch();
        }, 0);
      });
    },

    async flushPreviewUrlBatch() {
      this.coalesceTimer = null;
      const idsToFetch = [...this.pendingIds];
      this.pendingIds.clear();
      const waiters = this.coalesceWaiters.splice(0);

      try {
        if (idsToFetch.length > 0) {
          const { urls } = await timedOrdersFetch(
            `previews/urls ids=${idsToFetch.length}`,
            () => getPreviewUrls(idsToFetch),
            { idCount: idsToFetch.length },
          );
          for (const [idStr, entry] of Object.entries(urls)) {
            this.urlCache.set(Number(idStr), entry);
          }
        }

        for (const waiter of waiters) {
          const result: Record<number, string> = {};
          for (const id of waiter.ids) {
            const cached = this.urlCache.get(id);
            if (cached) result[id] = cached.url;
          }
          waiter.resolve(result);
        }
      } catch (error) {
        for (const waiter of waiters) waiter.reject(error);
      }
    },
  },
});
